import type {
  MeInformation,
  ResetPasswordForm,
  ResetPasswordInfo,
  UpdateUserSettingsRequest,
  UserSettings,
  UserSettingsUiChartType,
  UserSettingsUiTheme,
} from '@api'
import type { UseMutationResult } from 'react-query'
import { z } from 'zod'

import { emailSchema } from '@/types/schemas.ts'

type MutationFn<TResponse, TData> = UseMutationResult<
  TResponse,
  unknown,
  TData,
  unknown
>

export const registerFormSchema = z
  .object({
    name: z.string().min(2, { message: 'Name is required' }),
    email: emailSchema,
    'g-recaptcha-response': z.string(),
    password: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters' }),
    confirmPassword: z
      .string()
      .min(1, { message: 'Confirm Password is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: "Passwords don't match",
  })

export type RegisterFormValues = z.infer<typeof registerFormSchema>

export interface AccountContext {
  login: MutationFn<AccountAuthResponse, AccountLoginRequest>
  loginWithToken: (token: string) => void
  agencyLogoCurrent?: string | null
  resetPasswordMail: MutationFn<void, AccountResetPasswordMailRequest>
  resetPassword: MutationFn<ResetPasswordInfo, ResetPasswordForm>
  activateAccount: MutationFn<void, AccountActivationRequest>
  updateUserSettings: (
    settings: UpdateUserSettingsRequest,
    onSuccess?: () => void,
  ) => void
  isLoggedIn: boolean | undefined
  checkedAuthentication: boolean
  error: Error | null
  account: MeInformation | null
  isAccountLoaded: boolean
  isManualLogin: boolean
  userSettings: UserSettings | null
  themeData: Theme | null
  owner: Owner | undefined
  updateOwner: (owner: OwnerRequest) => void
  accessToken: string | null
  logout: () => void
  refetchAccount: () => void
  register: MutationFn<AccountAuthResponse, AccountRegisterRequest>
  setAsLoggedIn: () => void
  registerAfterPayment: (
    registerData: AccountRegisterAfterPaymentRequest,
  ) => Promise<AccountAuthResponse>
  setTfaEnabled: (enabled: boolean) => void
}

export interface AccountState {
  isLoggedIn: boolean | undefined
  checkedAuthentication: boolean
  error: Error | null
  account: MeInformation | null
  isAccountLoaded: boolean
  accessToken: string | null
  isManualLogin: boolean
  userSettings: UserSettings | null
  themeData: Theme | null
}

export interface AccountLoginRequest {
  email: string
  password: string
  /** @description The Google Recaptcha Response */
  'g-recaptcha-response'?: string
}

export interface AccountAuthResponse {
  /**
   * Format: uuid
   * @description The access token. By default valid for 30 days.
   * @example 275d37d1-09d6-435e-8c29-7e8fbd6f78fe
   */
  'auth-token': string | undefined
  'tfa-enabled'?: boolean
  'tfa-setup-required'?: boolean
}

export interface AccountRegisterRequest {
  name: string
  email: string
  password: string
}

export interface AccountRegisterAfterPaymentRequest {
  'g-recaptcha-response': string
  token: string
  emailToken: string
  newPassword: string
}

export interface AccountResetPasswordMailRequest {
  email: string
}

export interface AccountActivationRequest {
  token: string
  name: string
  password: string
}

export interface Owner {
  invoiceUrls: string[]
  emailConfirmed: boolean
  showOnboardingButton: boolean
  openOnboardingPopup: boolean
  bookedOnboardingCall: boolean
}

export interface Theme {
  chartType: UserSettingsUiChartType
  theme: UserSettingsUiTheme
}

export interface IntercomResponse {
  intercom: {
    appId: string
    apiBase: string
    userHash: string
    userId: string
    userCreatedAt: string
    email: string
    name: string
  }
}

export enum CreatorFeatures {
  expiring_fans = 'expiring_fans',
  better_vault = 'better_vault',
  welcome_messages = 'welcome_messages',
  follow_up_messages = 'follow_up_messages',
  rebill_messages = 'rebill_messages',
  stats = 'stats',
  audios = 'audios',
  fan_numbering = 'fan_numbering',
  auto_follow = 'auto_follow',
  tiktok = 'tiktok',
  instagram = 'instagram',
  chat_tracking = 'chat_tracking',
  tracking = 'tracking',
  projects = 'projects',
  tasks = 'tasks',
  tiers = 'tiers',
  media_cloning = 'media_cloning',
  bulk_messages = 'bulk_messages',
  script_tracking = 'script_tracking',
  promotion_automations = 'promotion_automations',
  scripts = 'scripts',
  lists = 'lists',
  display_settings = 'display_settings',
  automation_settings = 'automation_settings',
  visibility = 'visibility',
  prospecting = 'prospecting',
  accounting_site = 'accounting_site',
  tiktok_research = 'tiktok_research',
}

export interface OwnerRequest {
  invoiceUrls?: string[]
  emailConfirmed?: boolean
  showOnboardingButton?: boolean
  openOnboardingPopup?: boolean
  bookedOnboardingCall?: boolean
}

export interface CheckIsCaptchaRequiredResponse {
  requiresCaptcha: boolean
}
